import { PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import {
  getBucketName,
  getS3Client,
  getS3CacheUrl,
  getS3CacheToken,
} from '@lib/s3';

// ============================================================================
// 상수
// ============================================================================

const LATEST_FILE_NAME = 'latest';

// 캐시 최상위 폴더. 로컬 격리 테스트 시 WORDPRESS_CACHE_FOLDER_PREFIX 로 덮어씀
// (예: "cache/local-test/ria/") — 배포 dev/prod 는 미설정이라 기본 "cache/" 로 무영향.
// 이웃 s3 헬퍼(getBucketName 등)처럼 함수로 두어 dotenv 로드 순서(ESM 호이스팅) 문제를 피함.
const getCacheFolder = () =>
  process.env.WORDPRESS_CACHE_FOLDER_PREFIX || 'cache/';

// Path Traversal 방지용 패턴 (영문, 숫자, 하이픈, 언더스코어만 허용)
// 한글 slug 지원을 위해 유니코드 문자도 허용
// export 인 이유: 캐시 무효화 API 가 "캐시로 존재할 수 있는 이름인가"를 같은 기준으로
// 검사해야 한다 — 복제하면 한쪽만 좁혀졌을 때 조용히 어긋난다
export const FOLDER_NAME_PATTERN = /^[\w\-\u0080-\uFFFF]+$/;

// ============================================================================
// Logger 유틸리티
// ============================================================================

const logger = {
  uploadDone: (key: string, latestKey: string) =>
    console.log(`✅ S3 캐시 업로드 완료: ${key} ${latestKey}`),
  uploadError: () => console.error('❌ S3 캐시 업로드 에러'),

  fetchMiss: (url: string) => console.info(`ℹ️ S3 캐시 미스: ${url}`),
  fetchError: (url: string, status?: number) =>
    console.error(
      `❌ S3 캐시 조회 에러${status ? ` (${status})` : ''}: ${url}`,
    ),

  deleteDone: (key: string) => console.log(`🗑️ S3 캐시 삭제 완료: ${key}`),
  deleteError: () => console.error('❌ S3 캐시 삭제 에러'),
};

// ============================================================================
// 헬퍼 함수
// ============================================================================

/**
 * folderName 검증 (Path Traversal 방지)
 */
const validateFolderName = (folderName: string): void => {
  if (!folderName || !FOLDER_NAME_PATTERN.test(folderName)) {
    throw new Error('Invalid folder name');
  }
};

/**
 * 현재 날짜/시간을 파일명 형식으로 반환
 * 결과: "2025-12-16_11-05-30"
 */
const getCurrentDateTime = (): string => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const min = String(now.getMinutes()).padStart(2, '0');
  const ss = String(now.getSeconds()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}_${hh}-${min}-${ss}`;
};

/**
 * S3 업로드용 파라미터 생성
 */
const createS3Params = (key: string, body: string) => ({
  Bucket: getBucketName(),
  Key: key,
  Body: body,
  ContentType: 'application/json',
});

/**
 * S3 키 생성 헬퍼
 */
const createS3Key = {
  timestamped: (folderName: string, timestamp: string) =>
    `${getCacheFolder()}${folderName}/${timestamp}`,
  latest: (folderName: string) =>
    `${getCacheFolder()}${folderName}/${LATEST_FILE_NAME}`,
};

/**
 * 페이지네이션된 캐시의 공통 태그 prefix
 *
 * S3 캐시 폴더명이 `{prefix}-{pageIndex}` 패턴인 경우,
 * prefix를 공통 태그로 추출하여 전체 페이지 무효화에 사용
 */
const PAGINATED_CACHE_PREFIXES = [
  // Blog/News 리스트
  'getBlogNewsPostsForList-blog',
  'getBlogNewsPostsForList-news',
  // FAQ
  'getFaqItemsForList',
  // Publications
  'getPublications',
  // IR (items + media)
  'getIrItems-items-fetchItems-ir',
  'getIrItems-mediaItems-fetchMediaItems',
  // Resources (items + media)
  'getResourceItems-items-fetchItems-resources',
  'getResourceItems-mediaItems-fetchMediaItems',
  // Reanalysis
  'getReanalysisData-fetchItems-reanalysis',
];

/**
 * folderName에서 공통 태그 추출
 * - 'getBlogNewsPostsForList-blog-1' → 'getBlogNewsPostsForList-blog'
 * - 'getFaqItemsForList-1' → 'getFaqItemsForList'
 * - 'getIrItems-items-fetchItems-ir-101' → 'getIrItems-items-fetchItems-ir'
 * - 'getBlogNewsPost-blog-my-slug' → null (개별 포스트는 공통 태그 불필요)
 */
const getCommonTag = (folderName: string): string | null => {
  for (const prefix of PAGINATED_CACHE_PREFIXES) {
    if (!folderName.startsWith(prefix)) continue;
    const suffix = folderName.slice(prefix.length);
    if (
      suffix.length > 1 &&
      suffix[0] === '-' &&
      /^\d+$/.test(suffix.slice(1))
    ) {
      return prefix;
    }
  }
  return null;
};

/**
 * folderName을 HTTP 헤더에서 사용 가능한 ASCII 문자열로 인코딩
 * - 한글 등 non-ASCII 문자가 포함된 경우 Base64로 인코딩
 * - ASCII만 포함된 경우 그대로 반환
 *
 * revalidateTag() 호출 시에도 동일한 함수를 사용해야 캐시가 올바르게 무효화됩니다.
 */
export const encodeCacheTag = (tag: string): string => {
  // eslint-disable-next-line no-control-regex
  if (!/^[\u0000-\u007F]*$/.test(tag)) {
    return `b64_${Buffer.from(tag).toString('base64').replace(/[+/=]/g, '_')}`;
  }
  return tag;
};

// ============================================================================
// S3 캐시 업로드
// ============================================================================

export const uploadWordpressCacheDataToS3 = async <T>(
  data: T,
  folderName: string,
): Promise<string> => {
  validateFolderName(folderName);

  const jsonData = JSON.stringify(data, null, 2);
  const timestamp = getCurrentDateTime();
  const timestampedKey = createS3Key.timestamped(folderName, timestamp);
  const latestKey = createS3Key.latest(folderName);

  try {
    await Promise.all([
      getS3Client().send(
        new PutObjectCommand(createS3Params(timestampedKey, jsonData)),
      ),
      getS3Client().send(
        new PutObjectCommand(createS3Params(latestKey, jsonData)),
      ),
    ]);
    logger.uploadDone(timestampedKey, latestKey);
    return timestampedKey;
  } catch {
    logger.uploadError();
    throw new Error('S3 캐시 업로드 실패');
  }
};

// ============================================================================
// S3 캐시 조회
// ============================================================================

/**
 * S3에서 캐시된 WordPress 데이터 조회
 * @param folderName - 캐시 폴더명 (예: 'getBlogNewsPost-blog-my-post')
 *
 * folderName이 자동으로 태그로 사용됩니다.
 * 리스트 캐시의 경우 공통 태그도 추가됩니다.
 * - 개별 태그: getBlogNewsPostsForList-blog-1
 * - 공통 태그: getBlogNewsPostsForList-blog (모든 페이지 무효화용)
 *
 * revalidateTag(folderName) 또는 revalidateTag(commonTag)을 호출하면 캐시가 무효화됩니다.
 */
export const fetchWordpressCacheData = async <T = unknown>(
  folderName: string,
): Promise<T | null> => {
  const key = createS3Key.latest(folderName);
  const url = getS3CacheUrl(key);

  try {
    validateFolderName(folderName);
    const token = getS3CacheToken();

    // 태그 설정: 개별 태그 + 공통 태그 (리스트의 경우)
    const tags = [encodeCacheTag(folderName)];
    const commonTag = getCommonTag(folderName);
    if (commonTag) {
      tags.push(encodeCacheTag(commonTag));
    }

    const response = await fetch(url, {
      cache: 'force-cache',
      next: { tags },
      headers: {
        'X-Cache-Token': token || '',
      },
    });

    if (!response.ok) {
      // CloudFront 는 오리진에 ListBucket 권한이 없어 "없는 객체"에 404 대신 403 을 반환한다
      // — 403 은 실패가 아니라 캐시 미스다.
      if (response.status === 403) {
        logger.fetchMiss(url);
      } else {
        logger.fetchError(url, response.status);
      }
      return null;
    }

    const result: T = await response.json();
    return result;
  } catch {
    logger.fetchError(url);
    return null;
  }
};

// ============================================================================
// S3 캐시 삭제
// ============================================================================

/**
 * S3에서 캐시 파일 삭제
 * @param folderName - 캐시 폴더명 (예: 'getBlogNewsPost-blog-my-post')
 *
 * latest 파일만 삭제합니다. (타임스탬프 파일은 보존)
 */
export const deleteWordpressCacheFromS3 = async (
  folderName: string,
): Promise<boolean> => {
  try {
    validateFolderName(folderName);

    const key = createS3Key.latest(folderName);

    await getS3Client().send(
      new DeleteObjectCommand({
        Bucket: getBucketName(),
        Key: key,
      }),
    );

    logger.deleteDone(key);
    return true;
  } catch {
    logger.deleteError();
    return false;
  }
};
